import { Space, Tag, Typography } from "antd";
import { Property } from "csstype";
import React from "react";
import { Pie } from "react-chartjs-2";

interface PieChartProps {
  data: number[];
  labels: string[];
  height: number;
  colors?: string[];
  noDataText?: string;
  minWidth: Property.MinWidth | number;
  valueFormatter?: (value: number) => string;
}

export const PieChart: React.FC<PieChartProps> = ({
  data,
  labels,
  height,
  colors,
  noDataText,
  minWidth,
  valueFormatter,
}) => {
  const chartData = {
    labels: labels,
    datasets: [
      {
        data: data,
        backgroundColor: colors
          ? colors.map((color) => color + "99")
          : ["rgba(75, 192, 192, 0.6)", "rgba(153, 102, 255, 0.6)"],
        borderColor: colors,
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    legend: {
      display: true,
      position: "bottom",
      labels: {
        generateLabels: (chart: any) => {
          const data = chart.data;
          if (data.labels.length && data.datasets.length) {
            return data.labels.map((label: string, i: number) => {
              const value = data.datasets[0].data[i];
              const total = data.datasets[0].data.reduce(
                (prev: number, curr: number) => prev + curr
              );
              const percentage = Math.round((value / total) * 100);
              return {
                text: `${label}: ${percentage}%`,
                fillStyle: data.datasets[0].backgroundColor[i],
              };
            });
          }
          return [];
        },
      },
    },
    tooltips: {
      enabled: false, // Disable default tooltips to use custom legend for percentages
    },
  };

  const total = data.reduce((a, b) => a + b, 0);
  const formatValue = (value: number) => {
    if (valueFormatter) return valueFormatter(value);
    return Number.isInteger(value) ? value.toString() : value.toFixed(1);
  };

  const getPercent = (index: number) => {
    if (total === 0) return 0;
    return Math.round((data[index] / total) * 100);
  };

  return (
    <>
      {total === 0 && noDataText ? (
        <Typography.Text
          strong
          style={{
            display: "block",
            alignContent: "center",
            lineHeight: 1.5,
            marginTop: 8,
            minWidth,
            textAlign: "center",
          }}
        >
          {noDataText}
        </Typography.Text>
      ) : (
        <div style={{ minWidth }}>
          <div
            style={{
              marginBottom: "10px",
            }}
          >
            <Pie
              data={chartData}
              options={chartOptions}
              height={height}
              width={height}
            />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Space wrap size={[8, 8]} style={{ justifyContent: "center" }}>
              {labels.map((label, index) => (
                <Tag
                  key={index}
                  style={{ marginInlineEnd: 0, whiteSpace: "normal" }}
                >
                  {`${label}: ${formatValue(data[index])} (${getPercent(
                    index
                  )}%)`}
                </Tag>
              ))}
            </Space>
          </div>
        </div>
      )}
    </>
  );
};
